import React, {useState, useEffect} from 'react';
import {Row, Col, Tab, Tabs} from 'react-bootstrap';
import {Button, Badge} from '@/components/ui';
import {Icon} from '@/components/icons/Icon';
import {useAlert} from '@/components/alerts';
import {InlineAlert} from '@/components/alerts';
import {BaseModal} from '@/components/modals';
import {SelectField} from '@/components/forms';
import axios from 'axios';
import i18n from 'i18n';
import {buildURL} from '@/utils/urlBuilder';
import Spinner from '@/components/ui/Spinner';
import TableComponent from '@/components/table/Table';
import {extractErrorMessage} from '@/utils/errorHandling';

interface Role {
    id: number;
    name: string;
    description?: string;
    user_count: number;
    permission_count: number;
    created_on: string | null;
    updated_on: string | null;
}

interface Permission {
    codename: string;
    name: string;
    category: string;
    status: 'added' | 'removed' | 'common';
}

interface SelectOption {
    value: number;
    label: string;
}

interface ComparisonResult {
    role1: {
        id: number;
        name: string;
        description: string;
        permission_count: number;
    };
    role2: {
        id: number;
        name: string;
        description: string;
        permission_count: number;
    };
    comparison: {
        only_in_role1: Permission[];
        only_in_role2: Permission[];
        common_permissions: Permission[];
        summary: {
            total_role1: number;
            total_role2: number;
            common_count: number;
            only_role1_count: number;
            only_role2_count: number;
            similarity_percentage: number;
        };
    };
}

interface RoleComparisonModalProps {
    modalOpen: boolean;
    closeModal: () => void;
    availableRoles: Role[];
    preselectedRole?: Role | null;
}

const RoleComparisonModal: React.FC<RoleComparisonModalProps> = ({
    modalOpen,
    closeModal,
    availableRoles,
    preselectedRole = null
}) => {
    const {showError} = useAlert();
    const [loading, setLoading] = useState<boolean>(false);
    const [role1, setRole1] = useState<SelectOption | null>(null);
    const [role2, setRole2] = useState<SelectOption | null>(null);
    const [comparisonResult, setComparisonResult] = useState<ComparisonResult | null>(null);
    const [activeTab, setActiveTab] = useState<string>('differences');

    useEffect(() => {
        if (!modalOpen) {
            setRole1(null);
            setRole2(null);
            setComparisonResult(null);
            setActiveTab('differences');
        } else if (modalOpen && preselectedRole) {
            // If a role is preselected, set it as role1
            setRole1({
                value: preselectedRole.id,
                label: `${preselectedRole.name} (${preselectedRole.permission_count} ${i18n.t('role_comparison_permissions.text')})`
            });
            setRole2(null);
            setComparisonResult(null);
            setActiveTab('differences');
        }
    }, [modalOpen, preselectedRole]);

    const handleCompare = async (): Promise<void> => {
        if (!role1 || !role2) {
            showError(i18n.t('role_comparison_select_both_roles.text'));
            return;
        }

        if (role1.value === role2.value) {
            showError(i18n.t('role_comparison_cannot_compare_same.text'));
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(buildURL('adminRolesCompare'), {
                role1_id: role1.value,
                role2_id: role2.value
            });

            setComparisonResult(response.data);
            setActiveTab('differences');
        } catch (error: any) {
            console.error('Failed to compare roles:', error);
            const errorMessage = extractErrorMessage(error, 'Failed to compare roles');
            showError(i18n.t('role_comparison_error.text', {error: errorMessage}));
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'added':
                return <Badge variant="success">{i18n.t('role_comparison_added.text')}</Badge>;
            case 'removed':
                return <Badge variant="danger">{i18n.t('role_comparison_removed.text')}</Badge>;
            case 'common':
                return <Badge variant="secondary">{i18n.t('role_comparison_common.text')}</Badge>;
            default:
                return null;
        }
    };

    const permissionColumns = [
        {
            header: i18n.t('role_comparison_permission_name.text'),
            accessorKey: 'name',
            cell: ({row}: { row: { original: Permission } }) => {
                const permission = row.original;
                return (
                    <div>
                        <div className="fw-medium">{permission.name}</div>
                        <code className="text-muted small">{permission.codename}</code>
                    </div>
                );
            },
        },
        {
            header: i18n.t('role_comparison_status.text'),
            accessorKey: 'status',
            cell: ({getValue}: { getValue: () => string }) => getStatusBadge(getValue()),
        },
    ];

    const role1Options: SelectOption[] = availableRoles
        .filter(role => role.id !== role2?.value)
        .map(role => ({
            value: role.id,
            label: `${role.name} (${role.permission_count} ${i18n.t('role_comparison_permissions.text')})`
        }));

    const role2Options: SelectOption[] = availableRoles
        .filter(role => role.id !== role1?.value)
        .map(role => ({
            value: role.id,
            label: `${role.name} (${role.permission_count} ${i18n.t('role_comparison_permissions.text')})`
        }));

    return (
        <BaseModal
            isOpen={modalOpen}
            onClose={closeModal}
            size="xl"
            title={
                <>
                    <Icon name="mdi-compare" className="mr-2" />
                    {preselectedRole
                        ? i18n.t('role_comparison_title_with_role.text', {roleName: preselectedRole.name})
                        : i18n.t('role_comparison_title.text')
                    }
                </>
            }
            footer={
                <Button variant="secondary" onClick={closeModal}>
                    {i18n.t('jt_close.text')}
                </Button>
            }
        >
            {/* Role Selection */}
            <Row className="mb-4">
                <Col md={6}>
                    <SelectField
                        name="role1"
                        label={preselectedRole
                            ? i18n.t('role_comparison_base_role.text')
                            : i18n.t('role_comparison_first_role.text')
                        }
                        value={role1}
                        onChange={setRole1}
                        options={role1Options}
                        placeholder={i18n.t('role_comparison_select_role.text')}
                        isDisabled={loading || !!preselectedRole}
                        isClearable={!preselectedRole}
                        isSearchable={true}
                    />
                </Col>
                <Col md={6}>
                    <SelectField
                        name="role2"
                        label={preselectedRole
                            ? i18n.t('role_comparison_compare_with_role.text')
                            : i18n.t('role_comparison_second_role.text')
                        }
                        value={role2}
                        onChange={setRole2}
                        options={role2Options}
                        placeholder={i18n.t('role_comparison_select_role.text')}
                        isDisabled={loading}
                        isClearable={true}
                        isSearchable={true}
                    />
                </Col>
            </Row>

            {/* Compare Button */}
            <div className="text-center mb-4">
                <Button
                    variant="primary"
                    onClick={handleCompare}
                    disabled={!role1 || !role2 || loading}
                    className="px-4 float-right"
                >
                    {loading ? (
                        <>
                            <Icon name="mdi-loading" className="mr-2 spin" />
                            {i18n.t('role_comparison_comparing.text')}
                        </>
                    ) : (
                        <>
                            <Icon name="mdi-compare" className="mr-2" />
                            {i18n.t('role_comparison_compare_button.text')}
                        </>
                    )}
                </Button>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="text-center py-4">
                    <Spinner text={i18n.t('role_comparison_comparing.text')} centered />
                </div>
            )}

            {/* Comparison Results */}
            {comparisonResult && !loading && (
                <>
                    {/* Summary Cards */}
                    <Row className="mb-4">
                        <Col md={6}>
                            <div className="card border-primary">
                                <div className="card-body text-center">
                                    <h5 className="card-title text-primary">
                                        {comparisonResult.role1.name}
                                    </h5>
                                    <p className="card-text text-muted small">
                                        {comparisonResult.role1.description}
                                    </p>
                                    <div className="d-flex justify-content-around">
                                        <div>
                                            <div className="fw-bold text-primary fs-4">
                                                {comparisonResult.comparison.summary.total_role1}
                                            </div>
                                            <small className="text-muted">
                                                {i18n.t('role_comparison_total_permissions.text')}
                                            </small>
                                        </div>
                                        <div>
                                            <div className="fw-bold text-danger fs-4">
                                                {comparisonResult.comparison.summary.only_role1_count}
                                            </div>
                                            <small className="text-muted">
                                                {i18n.t('role_comparison_unique_permissions.text')}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="card border-success">
                                <div className="card-body text-center">
                                    <h5 className="card-title text-success">
                                        {comparisonResult.role2.name}
                                    </h5>
                                    <p className="card-text text-muted small">
                                        {comparisonResult.role2.description}
                                    </p>
                                    <div className="d-flex justify-content-around">
                                        <div>
                                            <div className="fw-bold text-success fs-4">
                                                {comparisonResult.comparison.summary.total_role2}
                                            </div>
                                            <small className="text-muted">
                                                {i18n.t('role_comparison_total_permissions.text')}
                                            </small>
                                        </div>
                                        <div>
                                            <div className="fw-bold text-success fs-4">
                                                {comparisonResult.comparison.summary.only_role2_count}
                                            </div>
                                            <small className="text-muted">
                                                {i18n.t('role_comparison_unique_permissions.text')}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Similarity Alert */}
                    <InlineAlert
                        variant={
                            comparisonResult.comparison.summary.similarity_percentage >= 80 ? 'success' :
                                comparisonResult.comparison.summary.similarity_percentage >= 50 ? 'warning' : 'info'
                        }
                        title={`${i18n.t('role_comparison_similarity.text')}: ${comparisonResult.comparison.summary.similarity_percentage}%`}
                        message={`${comparisonResult.comparison.summary.common_count} ${i18n.t('role_comparison_common_permissions.text')} ${i18n.t('role_comparison_out_of.text')} ${comparisonResult.comparison.summary.total_role1 + comparisonResult.comparison.summary.total_role2 - comparisonResult.comparison.summary.common_count} ${i18n.t('role_comparison_total_unique.text')}`}
                        icon="mdi-chart-pie"
                        className="mb-4"
                    />

                    {/* Detailed Comparison Tabs */}
                    <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'differences')} className="mb-3">
                        <Tab
                            eventKey="differences"
                            title={
                                <span>
                                    <Icon name="mdi-compare" className="mr-1" />
                                    {i18n.t('role_comparison_differences.text')}
                                    <Badge variant="secondary" className="ml-3">
                                        {comparisonResult.comparison.summary.only_role1_count + comparisonResult.comparison.summary.only_role2_count}
                                    </Badge>
                                </span>
                            }
                        >
                            <Row>
                                <Col md={6}>
                                    <h6 className="text-danger">
                                        <Icon name="mdi-minus-circle" className="mr-1" />
                                        {i18n.t('role_comparison_only_in_role.text', {roleName: comparisonResult.role1.name})}
                                        <Badge variant="danger" className="ml-3">
                                            {comparisonResult.comparison.summary.only_role1_count}
                                        </Badge>
                                    </h6>
                                    {comparisonResult.comparison.only_in_role1.length > 0 ? (
                                        <div className="card">
                                            <div className="card-body">
                                                    <TableComponent
                                                        data={comparisonResult.comparison.only_in_role1}
                                                        columns={permissionColumns}
                                                        sortable={true}
                                                    />
                                            </div>
                                        </div>
                                    ) : (
                                        <InlineAlert
                                            variant="info"
                                            message={i18n.t('role_comparison_no_unique_permissions.text')}
                                            className="text-center"
                                        />
                                    )}
                                </Col>
                                <Col md={6}>
                                    <h6 className="text-success">
                                        <Icon name="mdi-plus-circle" className="mr-1" />
                                        {i18n.t('role_comparison_only_in_role.text', {roleName: comparisonResult.role2.name})}
                                        <Badge variant="success" className="ml-3">
                                            {comparisonResult.comparison.summary.only_role2_count}
                                        </Badge>
                                    </h6>
                                    {comparisonResult.comparison.only_in_role2.length > 0 ? (
                                        <div className="card">
                                            <div className="card-body">
                                                    <TableComponent
                                                        data={comparisonResult.comparison.only_in_role2}
                                                        columns={permissionColumns}
                                                        sortable={true}
                                                    />
                                            </div>
                                        </div>
                                    ) : (
                                        <InlineAlert
                                            variant="info"
                                            message={i18n.t('role_comparison_no_unique_permissions.text')}
                                            className="text-center"
                                        />
                                    )}
                                </Col>
                            </Row>
                        </Tab>

                        <Tab
                            eventKey="common"
                            title={
                                <span>
                                    <Icon name="mdi-check-circle" className="mr-1" />
                                    {i18n.t('role_comparison_common_permissions.text')}
                                    <Badge variant="secondary" className="ml-3">
                                        {comparisonResult.comparison.summary.common_count}
                                    </Badge>
                                </span>
                            }
                        >
                            {comparisonResult.comparison.common_permissions.length > 0 ? (
                                <div className="card">
                                    <div className="card-body">
                                            <TableComponent
                                                data={comparisonResult.comparison.common_permissions}
                                                columns={permissionColumns}
                                                sortable={true}
                                            />
                                    </div>
                                </div>
                            ) : (
                                <InlineAlert
                                    variant="warning"
                                    message={i18n.t('role_comparison_no_common_permissions.text')}
                                    icon="mdi-alert"
                                    className="text-center"
                                />
                            )}
                        </Tab>
                    </Tabs>
                </>
            )}
        </BaseModal>
    );
};

export default RoleComparisonModal;
